import { scriptLoadError } from '../../utils/common-utils';

type LiveChatInit = {
  init: (options: InitOptions) => Promise<void>;
};

type InitOptions = {
  businessUnitId: string;
  environment: 'int' | 'prod';
  languageCode?: string;
};

async function getResourceJson<T>(url: string, errorMessage: string): Promise<T> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(errorMessage);
    }
    return await response.json();
  } catch (error) {
    throw new Error(`${errorMessage}. Cause: ${error}`);
  }
}

function verifyConfig(config: unknown): asserts config is TenantConfig {
  if (typeof config !== 'object' || config === null) {
    throw new TypeError('Config must be a non-null object');
  }
}

function replaceLanguageCodeInBuid(businessUnitId: string, languageCode: string): string {
  return businessUnitId.replace(/^(([^-]+-){3})[^-]+/, `$1${languageCode}`);
}

async function loadAffiliateConfig({
  businessUnitId,
  environment
}: {
  businessUnitId: string;
  environment: InitOptions['environment'];
}) {
  const url = `https://${environment === 'int' ? 'uat.ncsa' : 'emea'}.sdapi.io/stardust-buid-configurations-v1/configurations/${businessUnitId}?path=ugc.global_chat`;
  const config = await getResourceJson(
    url,
    `Failed to fetch affiliate's LiveChat configuration from ${url}`
  );

  if (!config) {
    throw new Error('No initial configuration found in window.LiveChat. Cannot load LiveChat.');
  }

  verifyConfig(config);

  return config;
}

async function loadManifest(filename: string): Promise<Manifest> {
  const manifest = await getResourceJson<Manifest>(
    `/livechat/common/${filename}`,
    'Failed to fetch LiveChat manifest file.'
  );
  if (!manifest) {
    throw new Error('Failed to load manifest file. Cannot load LiveChat.');
  }
  return manifest;
}

async function createAssetLoaderScript(path: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const assetLoaderScript = document.createElement('script');
    assetLoaderScript.src = '/livechat' + path;
    assetLoaderScript.dataset.sourceMap = path + '.map';
    assetLoaderScript.type = 'text/javascript';
    assetLoaderScript.onload = () => resolve();
    assetLoaderScript.onerror = (event) => reject(scriptLoadError(event));
    document.head.appendChild(assetLoaderScript);
  });
}

export const LiveChatInit: LiveChatInit = {
  async init({ businessUnitId, languageCode, environment }: InitOptions): Promise<void> {
    try {
      window.LiveChat ??= {};

      const affiliateConfig = await loadAffiliateConfig({
        businessUnitId: languageCode
          ? replaceLanguageCodeInBuid(businessUnitId, languageCode)
          : businessUnitId,
        environment
      });

      window.LiveChat = affiliateConfig;

      const manifest = await loadManifest(affiliateConfig.asset_manifest_filename);
      window.LiveChat.manifest = manifest;

      await createAssetLoaderScript(manifest.asset_loader[0]);
    } catch (error: unknown) {
      const errorMessage = `LIVECHAT ERROR: ${(error as Error).message}`;
      console.error(errorMessage);
      window.Sentry?.captureException(errorMessage);
    }
  }
};
